import { WarrantyStatus, PaginatedResponse } from 'src/app/models/device/device.model';
import { Injectable } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RestSerialService } from 'src/app/service/rest/serial/rest-serial.service';
import { SerialNumber } from '../device/device.model';

export const SERIAL_NUMBERS_STORAGE_KEY = 'Upservice-serial-numbers';

@Injectable()
export class SerialNumberRepositoryService {
  
  

  private _serialNumbers: SerialNumber[] = [];
  private _possibleSerialNumbers: SerialNumber[] = [];
  private _installedSerialNumbers: SerialNumber[] = [];
  private _dataSourceSerialNumbers = new MatTableDataSource<SerialNumber>();
  private _dataSourceInstalledSerialNumbers =
    new MatTableDataSource<SerialNumber>();

  serialNumbersLoaded: boolean = false;
  possibleSerialNumbersLoaded: boolean = false;

  constructor(private serialRest: RestSerialService) {}

  public checkSerial(serial: string): Observable<any> {
    return this.serialRest.checkSerial(serial);
  }

  public getSerialNumberBySerialNumber(serial: string) {
    return this.serialRest.getSerialNumberBySerialNumber(serial);
  }

  public loadPossibleSerialNumbers(pageNumber: number, pageSize: number) {
    this.serialRest.getPossibleSerials(pageNumber, pageSize).subscribe(
      (response: PaginatedResponse<SerialNumber>) => {
        if (response?.data) {
          this.possibleSerialNumbers = response.data;
          this.possibleSerialNumbersLoaded = true;
        }
      },
      (error) => {
        console.log(error);
      }
    );
  }

  public loadSerialNumbers(pageNumber: number, pageSize: number): Observable<PaginatedResponse<SerialNumber>> {
    return this.serialRest.getAllSerials(pageNumber, pageSize);
  }

  // Backward compatibility method that returns only the data array
  public loadSerialNumbersAsArray(pageNumber: number, pageSize: number): Observable<SerialNumber[]> {
    return this.serialRest.getAllSerials(pageNumber, pageSize).pipe(
      map(response => response.data)
    );
  }

  public loadSerialNumbersByDeviceType(deviceTypeId : number, pageNumber: number, pageSize: number): Observable<PaginatedResponse<SerialNumber>> {
    return this.serialRest.getAllSerialsByDeviceTypePaginated(deviceTypeId, pageNumber, pageSize);
  }

  // Backward compatibility method that returns only the data array
  public loadSerialNumbersByDeviceTypeAsArray(deviceTypeId : number, pageNumber: number, pageSize: number): Observable<SerialNumber[]> {
    return this.serialRest.getAllSerialsByDeviceTypePaginated(deviceTypeId, pageNumber, pageSize).pipe(
      map(response => response.data)
    );
  }

  get serialNumbers(): SerialNumber[] {
    let data: SerialNumber[] = JSON.parse(
      sessionStorage.getItem(SERIAL_NUMBERS_STORAGE_KEY)
    );
    if (this._serialNumbers.length == 0 && !this.serialNumbersLoaded && data) {

      this.serialNumbersLoaded = true;

      this.serialNumbers = data;
      return data;
    } else return this._serialNumbers;
  }

  set serialNumbers(data: SerialNumber[]) {
   // sessionStorage.setItem(SERIAL_NUMBERS_STORAGE_KEY, JSON.stringify(data));
    this._serialNumbers = data;
  }

  public get dataSourceSerialNumbers() {
    return this._dataSourceSerialNumbers;
  }
  public set dataSourceSerialNumbers(value) {
    this._dataSourceSerialNumbers = value;
  }

  get possibleSerialNumbers(): SerialNumber[] {
    return this._possibleSerialNumbers;
  }

  set possibleSerialNumbers(data: SerialNumber[]) {
    this._possibleSerialNumbers = data;
  }

  public get installedSerialNumbers(): SerialNumber[] {
    return this._installedSerialNumbers;
  }
  public set installedSerialNumbers(value: SerialNumber[]) {
    this._installedSerialNumbers = value;
  }

  public get dataSourceInstalledSerialNumbers(): MatTableDataSource<SerialNumber> {
    return this._dataSourceInstalledSerialNumbers;
  }
  public set dataSourceInstalledSerialNumbers(
    value: MatTableDataSource<SerialNumber>
  ) {
    this._dataSourceInstalledSerialNumbers = value;
  }

  public getInstalledSerialNumbers(pageNumber: number, pageSize: number): Observable<PaginatedResponse<SerialNumber>> {
    return this.serialRest.getInstalledSerials(pageNumber, pageSize);
  }

  // Backward compatibility method that returns only the data array
  public getInstalledSerialNumbersAsArray(pageNumber: number, pageSize: number): Observable<SerialNumber[]> {
    return this.serialRest.getInstalledSerials(pageNumber, pageSize).pipe(
      map(response => response.data)
    );
  }

  public getPossibleSerialsWithPagination(pageNumber: number, pageSize: number): Observable<PaginatedResponse<SerialNumber>> {
    return this.serialRest.getPossibleSerials(pageNumber, pageSize);
  }

  public getAllSerialsWithPagination(pageNumber: number, pageSize: number): Observable<PaginatedResponse<SerialNumber>> {
    return this.serialRest.getAllSerials(pageNumber, pageSize);
  }

  filterResults(
    warrantyStatus: string,
    serialNumberToSearch: string,
    country: string,
    filterDate?: Date,
  ): boolean {
    let preFilter = this.installedSerialNumbers;
    if (serialNumberToSearch != '' && serialNumberToSearch != null) {
      preFilter = preFilter.filter((element) =>
      element.serialnumber.toLowerCase().includes(serialNumberToSearch.toLowerCase())
      );
    }
    if (filterDate != null) {
      preFilter = preFilter.filter(
        (serialNumber) =>
          new Date(serialNumber.warrantyExp).getTime() <= filterDate.getTime()
      );
    }
    if (warrantyStatus != WarrantyStatus.ALL) {
      const warrantyStatusKey = Object.keys(WarrantyStatus).find(
        (key) => WarrantyStatus[key] === warrantyStatus
      );
      preFilter = preFilter.filter(
        (serialNumber) => serialNumber.warrantyStatus === warrantyStatusKey
      );
    }
    if (country) {
      preFilter = preFilter.filter(serialNumber => serialNumber?.country.toLowerCase() === country.toLowerCase());
    }
    this.dataSourceInstalledSerialNumbers = new MatTableDataSource(preFilter);
    return true;
  }

  resetFilter(): boolean {
    this.dataSourceInstalledSerialNumbers = new MatTableDataSource(
      this.installedSerialNumbers
    );
    return true;
  }

  loadSerialNumbersByArticleCode(articleCode: string): Observable<SerialNumber[]> {
    return this.serialRest.getAllSerialsByArticleCode(articleCode);
  }

  loadSerialNumbersByArticleCodeWithPagination(articleCode: string, pageNumber: number, pageSize: number): Observable<PaginatedResponse<SerialNumber>> {
    return this.serialRest.getAllSerialsByArticleCodePaginated(articleCode, pageNumber, pageSize);
  }

  uploadSerials(formData: FormData): Observable<any> {
    return this.serialRest.uploadSerialNumbers(formData);
  }

  // Search serials with filters and pagination
  searchSerials(
    pageNumber: number,
    pageSize: number,
    sn?: string, 
    articleCode?: string, 
    deviceTypeId?: number, 
    installed?: boolean,
    warrantyExpBefore?: Date,
    warrantyExpAfter?: Date,
    warrantyStatus?: string,
    countryCode?: string,
    familyId?: number
  ): Observable<PaginatedResponse<SerialNumber>> {
    return this.serialRest.searchSerials(pageNumber, pageSize, sn, articleCode, deviceTypeId, installed, warrantyExpBefore, warrantyExpAfter, warrantyStatus, countryCode, familyId);
  }

  // Utility method to extract pagination info from any paginated response
  getPaginationInfo<T>(response: PaginatedResponse<T>): { totalItems: number, totalPages: number, currentPage: number, pageSize: number } {
    return {
      totalItems: response.totalItems,
      totalPages: response.totalPages,
      currentPage: response.currentPage,
      pageSize: response.pageSize
    };
  }

  // Utility method to update MatTableDataSource with paginated data
  updateDataSourceWithPagination<T>(dataSource: MatTableDataSource<T>, response: PaginatedResponse<T>): void {
    dataSource.data = response.data;
    // Note: MatPaginator will be updated separately with pagination info
  }
}
